import React from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import JobContextNote from './JobContextNote'
import DisabledReason from '../system/DisabledReason'

interface StudyContextEditorProps {
  /** The saved task context (stored as the task's study_context). */
  value: string
  /** The unsaved edit, held by the page so moving between stages keeps it; undefined when not editing. */
  draft?: string
  saving: boolean
  onDraftChange: (draft: string | undefined) => void
  onSave: () => Promise<void>
  /** The organisation and job context from Admin → Context, shown read-only. */
  jobContext?: string
  onOpenJobContext: () => void
  /** Why this person cannot save the context, or undefined. The box is then read-only and Save says why. */
  blockedReason?: string
}

// What the researcher tells ThemeAI about the task. The agent reads it when
// proposing themes and every run codes with it (spec #916 §1.3). Saving is
// explicit and an edit survives clicking away (Matt, 18 Sep: "I click out of
// it. I need to save the button"; #1478). The heading and the prompt above the
// box belong to the Data step, which leads with them (#1492 item 8).
//
// No example in the box. Matt, 23 Sep, on the old "e.g. Channel 4 viewers
// outside London" line: "Just don't have that. That's a loaded thing."
const StudyContextEditor: React.FC<StudyContextEditorProps> = ({ value, draft, saving, onDraftChange, onSave, jobContext, onOpenJobContext, blockedReason }) => {
  const text = draft ?? value
  // Save wakes up once there is something new to keep (Matt, 23 Sep: "the save
  // button becomes active, of course").
  const dirty = draft !== undefined && draft.trim() !== value.trim()

  const handleSave = async () => {
    if (!dirty || saving || blockedReason) return
    try {
      await onSave()
    } catch {
      // The hook reports the error; the draft stays for another try.
    }
  }

  return (
    <Box sx={{ maxWidth: 880, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        multiline
        minRows={3}
        maxRows={12}
        fullWidth
        value={text}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            handleSave()
          }
        }}
        inputProps={{ 'aria-label': 'About this task', readOnly: Boolean(blockedReason) }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 36 }}>
        <DisabledReason reason={blockedReason}>
          <Button variant="contained" color="info" size="small" disabled={!dirty || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DisabledReason>
        {dirty && (
          <Button color="info" size="small" disabled={saving} onClick={() => onDraftChange(undefined)}>
            Discard changes
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {dirty ? 'Unsaved changes · ⌘↵ to save' : value ? 'Saved' : ''}
        </Typography>
      </Box>
      <JobContextNote jobContext={jobContext} onOpenJobContext={onOpenJobContext} />
    </Box>
  )
}

export default StudyContextEditor
